import { getFirestore, doc, updateDoc, collection, addDoc, getDoc } from "firebase/firestore";

/**
 * Books a regular appointment by:
 * 1. Creating a new patient document or updating existing one in the 'patients' collection
 * 2. Updating the appointment document to mark it as booked and link it to the patient
 * 
 * @param {string} appointmentId - The ID of the appointment to book
 * @param {string} officeId - The ID of the office where the appointment is located
 * @param {Object} patientData - The patient data to save (from Patient.toJSON())
 * @param {string|null} existingPatientId - ID of existing patient if verifying a match
 * @returns {Promise<{success: boolean, patientId: string|null, error: Error|null}>}
 */
export async function bookAppointment(appointmentId, officeId, patientData, existingPatientId = null) {
  const db = getFirestore();
  let patientId = null;
  const isExistingFromMatch =
    typeof existingPatientId === "string" && existingPatientId.trim().length > 0;

  try {
    // Calculate age based on DOB
    const dob = patientData.dob;
    let age = null;
    
    if (dob) {
      const birthDate = new Date(dob);
      if (!isNaN(birthDate.getTime())) {
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
      }
    }
    
    // Add calculated age to patient data
    const patientDataWithAge = {
      ...patientData,
      age: age,
      updatedAt: new Date()
    };
    
    // Step 1: Create or update patient document
    if (isExistingFromMatch) {
      // Update existing patient
      const patientRef = doc(db, "patients", existingPatientId);
      
      // Get current patient data
      const currentPatientDoc = await getDoc(patientRef);
      const currentPatientData = currentPatientDoc.data();
      
      // Only update the fields that should always be updated and any that changed
      const updatedFields = {
        age: patientDataWithAge.age, // Always update age
        reason: patientDataWithAge.reason, // Always update reason
        updatedAt: patientDataWithAge.updatedAt
      };
      
      // Add other changed fields
      for (const [key, value] of Object.entries(patientDataWithAge)) {
        // Skip fields we've already included
        if (key === 'age' || key === 'reason' || key === 'updatedAt') continue;
        
        // Only include fields that have changed
        if (currentPatientData[key] !== value && value) {
          updatedFields[key] = value;
        }
      }
      
      await updateDoc(patientRef, updatedFields);
      console.log("Patient document updated with ID:", existingPatientId);
      patientId = existingPatientId;
    } else {
      // Create new patient (createdAt = first registration; volunteer dashboard can infer new vs existing when appt has no flag)
      const patientsCollection = collection(db, "patients");
      const createdAt = new Date();
      const patientDocRef = await addDoc(patientsCollection, {
        ...patientDataWithAge,
        createdAt,
      });
      patientId = patientDocRef.id;
      console.log("New patient document created with ID:", patientId);
    }
    
    // Step 2: Update the appointment document to mark it as booked and link it to the patient
    const appointmentRef = doc(db, "offices", officeId, "appointments", appointmentId);
    await updateDoc(appointmentRef, { 
      booked: true,
      isWalkIn: false,
      patientId: patientId,
      patientName: `${patientData.firstName} ${patientData.lastName}`,
      visitReason: patientData.reason || '',
      isExistingPatient: isExistingFromMatch,
      updatedAt: new Date()
    });
    
    console.log("Appointment booked successfully!");
    return {
      success: true,
      patientId: patientId,
      error: null
    };
  } catch (error) {
    console.error("Error booking appointment:", error);
    
    return {
      success: false,
      patientId: patientId,
      error: error
    };
  }
}

/**
 * Books an event appointment by:
 * 1. Creating a new patient document or updating existing one in the 'patients' collection
 * 2. Creating an appointment document in the event's appointments subcollection
 * 
 * @param {string} eventId - The ID of the event
 * @param {string} eventBookingLinkId - The booking link ID of the event
 * @param {Object} patientData - The patient data to save (from Patient.toJSON())
 * @param {Object} slotData - The selected slot data
 * @param {string|null} existingPatientId - ID of existing patient if verifying a match
 * @returns {Promise<{success: boolean, patientId: string|null, appointmentId: string|null, error: Error|null}>}
 */
export async function bookEventAppointmentWithPatient(eventId, eventBookingLinkId, patientData, slotData, existingPatientId = null) {
  const db = getFirestore();
  let patientId = null;
  let appointmentId = null;
  
  try {
    // Calculate age based on DOB
    const dob = patientData.dob;
    let age = null;
    
    if (dob) {
      const birthDate = new Date(dob);
      if (!isNaN(birthDate.getTime())) {
        const today = new Date();
        age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
      }
    }
    
    // Add calculated age to patient data
    const patientDataWithAge = {
      ...patientData,
      age: age,
      updatedAt: new Date()
    };
    
    // Step 1: Create or update patient document
    if (existingPatientId) {
      // Update existing patient
      const patientRef = doc(db, "patients", existingPatientId);
      
      // Get current patient data
      const currentPatientDoc = await getDoc(patientRef);
      const currentPatientData = currentPatientDoc.data();
      
      // Only update the fields that should always be updated and any that changed
      const updatedFields = {
        age: patientDataWithAge.age, // Always update age
        reason: patientDataWithAge.reason, // Always update reason
        updatedAt: patientDataWithAge.updatedAt
      };
      
      // Add other changed fields
      for (const [key, value] of Object.entries(patientDataWithAge)) {
        // Skip fields we've already included
        if (key === 'age' || key === 'reason' || key === 'updatedAt') continue;
        
        // Only include fields that have changed
        if (currentPatientData[key] !== value && value) {
          updatedFields[key] = value;
        }
      }
      
      await updateDoc(patientRef, updatedFields);
      console.log("Patient document updated with ID:", existingPatientId);
      patientId = existingPatientId;
    } else {
      // Create new patient
      const patientsCollection = collection(db, "patients");
      const patientDocRef = await addDoc(patientsCollection, patientDataWithAge);
      patientId = patientDocRef.id;
      console.log("New patient document created with ID:", patientId);
    }
    
    // Step 2: Create appointment document in the event's appointments subcollection
    const appointmentsRef = collection(db, "events", eventId, "appointments");
    const appointmentData = {
      eventId: eventId,
      eventBookingLinkId: eventBookingLinkId,
      patientName: `${patientData.firstName} ${patientData.lastName}`,
      patientPhone: patientData.phone,
      patientEmail: patientData.email,
      slotStart: slotData.startTime,
      slotEnd: slotData.endTime,
      patientId: patientId,
      createdAt: new Date(),
      patientData: patientData // Store full patient data for reference
    };
    
    const appointmentDocRef = await addDoc(appointmentsRef, appointmentData);
    appointmentId = appointmentDocRef.id;
    
    console.log("Event appointment booked successfully with ID:", appointmentId);
    return {
      success: true,
      patientId: patientId,
      appointmentId: appointmentId,
      error: null
    };
  } catch (error) {
    console.error("Error booking event appointment:", error);
    
    return {
      success: false,
      patientId: patientId,
      appointmentId: appointmentId,
      error: error
    };
  }
}
